import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface SchoolContractor {
  id: string
  fullName: string
  phone: string | null
  email: string | null
  address: string | null
  instructorLicenceNumber: string | null
  instructorLicenceExpiryDate: string | null
  driverLicenceNumber: string | null
  driverLicenceExpiryDate: string | null
  vehicleMake: string | null
  vehicleModel: string | null
  vehicleYear: number | null
  vehiclePlate: string | null
  serviceAreas: string[]
  hourlyRate: number | null
  notes: string | null
  isActive: boolean
}

export interface ContractorAssignment {
  id: string
  contractorId: string
  packageId: string | null
  packageName: string | null
  packagePrice: number | null
  packageInCarHours: number | null
  studentName: string
  hourlyRateSnapshot: number | null
  startedAt: string
  endedAt: string | null
  notes: string | null
}

interface RpcResult {
  ok: boolean
  code?: string
}

/**
 * A school's private, account-less roster of contractor instructors it pays directly
 * (migration 101), plus the student/package assignments recorded against them for
 * commission bookkeeping. Write path is plain RLS (no RPC) — same shape as
 * useSchoolPackages — since this is simple admin-owned config/bookkeeping with no
 * cross-table derivation.
 */
export function useSchoolContractors(schoolId: string | null) {
  const [contractors, setContractors] = useState<SchoolContractor[]>([])
  const [assignments, setAssignments] = useState<ContractorAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!schoolId) { setContractors([]); setAssignments([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const [contractorsRes, assignmentsRes] = await Promise.all([
        supabase
          .from('school_contractors')
          .select('id, full_name, phone, email, address, instructor_licence_number, instructor_licence_expiry_date, driver_licence_number, driver_licence_expiry_date, vehicle_make, vehicle_model, vehicle_year, vehicle_plate, service_areas, hourly_rate, notes, is_active')
          .eq('school_id', schoolId)
          .order('full_name', { ascending: true }),
        supabase
          .from('contractor_package_assignments')
          .select('id, contractor_id, package_id, student_name, hourly_rate_snapshot, started_at, ended_at, notes, school_packages(name, price, in_car_hours)')
          .eq('school_id', schoolId)
          .order('started_at', { ascending: false }),
      ])

      if (contractorsRes.error || assignmentsRes.error) { setError('Could not load contractors.'); return }

      setContractors((contractorsRes.data ?? []).map(c => ({
        id: c.id,
        fullName: c.full_name,
        phone: c.phone,
        email: c.email,
        address: c.address,
        instructorLicenceNumber: c.instructor_licence_number,
        instructorLicenceExpiryDate: c.instructor_licence_expiry_date,
        driverLicenceNumber: c.driver_licence_number,
        driverLicenceExpiryDate: c.driver_licence_expiry_date,
        vehicleMake: c.vehicle_make,
        vehicleModel: c.vehicle_model,
        vehicleYear: c.vehicle_year,
        vehiclePlate: c.vehicle_plate,
        serviceAreas: c.service_areas ?? [],
        hourlyRate: c.hourly_rate === null ? null : Number(c.hourly_rate),
        notes: c.notes,
        isActive: c.is_active,
      })))

      setAssignments((assignmentsRes.data ?? []).map(a => ({
        id: a.id,
        contractorId: a.contractor_id,
        packageId: a.package_id,
        packageName: (a as any).school_packages?.name ?? null,
        packagePrice: (a as any).school_packages?.price === null || (a as any).school_packages?.price === undefined
          ? null : Number((a as any).school_packages.price),
        packageInCarHours: (a as any).school_packages?.in_car_hours === null || (a as any).school_packages?.in_car_hours === undefined
          ? null : Number((a as any).school_packages.in_car_hours),
        studentName: a.student_name,
        hourlyRateSnapshot: a.hourly_rate_snapshot === null ? null : Number(a.hourly_rate_snapshot),
        startedAt: a.started_at,
        endedAt: a.ended_at,
        notes: a.notes,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolId])

  useEffect(() => { fetchAll() }, [fetchAll])

  const createContractor = useCallback(async (fields: {
    fullName: string
    phone?: string | null
    email?: string | null
    address?: string | null
    instructorLicenceNumber?: string | null
    instructorLicenceExpiryDate?: string | null
    driverLicenceNumber?: string | null
    driverLicenceExpiryDate?: string | null
    vehicleMake?: string | null
    vehicleModel?: string | null
    vehicleYear?: number | null
    vehiclePlate?: string | null
    serviceAreas?: string[]
    hourlyRate?: number | null
    notes?: string | null
  }): Promise<RpcResult> => {
    if (!schoolId || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: insertErr } = await supabase.from('school_contractors').insert({
        school_id: schoolId,
        full_name: fields.fullName,
        phone: fields.phone ?? null,
        email: fields.email ?? null,
        address: fields.address ?? null,
        instructor_licence_number: fields.instructorLicenceNumber ?? null,
        instructor_licence_expiry_date: fields.instructorLicenceExpiryDate ?? null,
        driver_licence_number: fields.driverLicenceNumber ?? null,
        driver_licence_expiry_date: fields.driverLicenceExpiryDate ?? null,
        vehicle_make: fields.vehicleMake ?? null,
        vehicle_model: fields.vehicleModel ?? null,
        vehicle_year: fields.vehicleYear ?? null,
        vehicle_plate: fields.vehiclePlate ?? null,
        service_areas: fields.serviceAreas && fields.serviceAreas.length > 0 ? fields.serviceAreas : null,
        hourly_rate: fields.hourlyRate ?? null,
        notes: fields.notes ?? null,
      })
      if (insertErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [schoolId, saving, fetchAll])

  const setContractorActive = useCallback(async (contractorId: string, isActive: boolean): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: updateErr } = await supabase.from('school_contractors')
        .update({ is_active: isActive })
        .eq('id', contractorId)
      if (updateErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const createAssignment = useCallback(async (fields: {
    contractorId: string
    packageId?: string | null
    studentName: string
    hourlyRateSnapshot?: number | null
    startedAt?: string
    notes?: string | null
  }): Promise<RpcResult> => {
    if (!schoolId || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: insertErr } = await supabase.from('contractor_package_assignments').insert({
        school_id: schoolId,
        contractor_id: fields.contractorId,
        package_id: fields.packageId ?? null,
        student_name: fields.studentName,
        hourly_rate_snapshot: fields.hourlyRateSnapshot ?? null,
        started_at: fields.startedAt,
        notes: fields.notes ?? null,
      })
      if (insertErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [schoolId, saving, fetchAll])

  const endAssignment = useCallback(async (assignmentId: string): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: updateErr } = await supabase.from('contractor_package_assignments')
        .update({ ended_at: new Date().toISOString().slice(0, 10) })
        .eq('id', assignmentId)
      if (updateErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  return {
    contractors, assignments, loading, error, saving,
    createContractor, setContractorActive, createAssignment, endAssignment,
    refresh: fetchAll,
  }
}
